import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Heart } from 'react-feather';

import { ClientReviewResponse, LikeInteraction } from '@vestico/model';

import { UserContext } from '@vestico/shared';

import { UserInteractionsContext } from '../../UserInteractionsProvider';
import { GenericOverlayIconProps, OverlayIcon } from './OverlayIcon';
import style from './PostLikeHeart.module.css';

export interface PostLikeHeartProps {
  response?: ClientReviewResponse;
}

export const PostLikeHeart = ({ response, size, colorScheme, disableShadows }: PostLikeHeartProps & GenericOverlayIconProps) => {
  const { recordInteraction, getUserLikedPost } = useContext(UserInteractionsContext);
  const { user, uid: userUID } = useContext(UserContext);

  const [likesPost, setLikesPost] = useState<boolean | undefined>(undefined);

  const likeCount = useMemo(() => (response ? Math.max(response.likes, 0) : 0), [response, response?.likes]);

  const hasLikedPost = useCallback(async () => {
    if (!user || !response) {
      return false;
    }

    if (!userUID) {
      return false;
    }

    const newLikesPost = await getUserLikedPost({ post: response.postDocumentID, user: userUID });
    return newLikesPost;
  }, [user, response, userUID, getUserLikedPost]);

  const onLikePost = useCallback(async () => {
    if (!response) {
      return;
    }

    // Update the UI before recording interaction server side
    const newLikesPost = !likesPost;
    setLikesPost(newLikesPost);
    response.likes += !likesPost ? 1 : -1;

    const success = await recordInteraction({
      post: response.postDocumentID,
      user: userUID,
      like: likesPost ? LikeInteraction.unlike : LikeInteraction.like,
    });

    // revert UI if not successfull
    if (!success) {
      setLikesPost(likesPost);
      response.likes -= !likesPost ? 1 : -1;
    }
  }, [response, likesPost, recordInteraction, userUID]);

  const onClick = useCallback(() => {
    onLikePost();
  }, [onLikePost]);

  useEffect(() => {
    setLikesPost(undefined);
    hasLikedPost().then((newLikesPost) => setLikesPost(newLikesPost));
  }, [hasLikedPost]);

  if (!response) {
    return null;
  }

  const text = likeCount > 1 ? likeCount.toString() : null;

  return (
    <OverlayIcon
      size={size}
      colorScheme={colorScheme}
      disableShadows={disableShadows}
      icon={<Heart className={clsx(style.icon, { [style.likesPost]: likesPost })} />}
      fillIcon
      text={text}
      onClick={onClick}
      ariaLabel="Like"
    />
  );
};
